import {
  BESTIARY_MILESTONES,
  BESTIARY_REWARDS,
  ELEMENT_NAMES,
  bestiaryStage,
  type BestiaryPacket,
  type DamageElement,
} from '@game/protocol';

import { collapsiblePanel } from './panel.ts';

/**
 * A tela de bestiário (PRD §15.5).
 *
 * ## O que ela mostra, e o que ela esconde
 *
 * Espécie nunca vista **não aparece**. A lista é um registro do que o jogador encontrou, e
 * mostrar as vinte e oito de saída entregaria de graça o mapa do mundo inteiro — a mesma
 * razão pela qual o minimapa só desenha o terreno já recebido (§13.12).
 *
 * Os três marcos — 1, 25 e 100 abates — revelam, nesta ordem: nome e aparência; vida,
 * ataque e o que ela larga; resistências e ritmo de retorno. A recompensa é **informação**,
 * e nunca poder: pagar poder faria caçar a mesma espécie mil vezes ser a forma ótima de
 * progredir, que é o oposto do que doze terrenos de caça existem para provocar.
 *
 * ## Por que o progresso do próximo marco fica visível
 *
 * Uma contagem sem alvo é um número. "34 de 100" é uma decisão — voltar àquele terreno ou
 * seguir em frente —, e é a diferença entre um contador e um objetivo.
 */
export class BestiaryPanel {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private open = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.className = 'bestiary';

    // Recolher e fechar são gestos diferentes aqui: `B` fecha a ficha inteira, a barra de
    // título deixa só o cabeçalho no canto da tela. É o que serve a quem está comparando a
    // resistência de um bicho com o que está acontecendo no mundo atrás do painel.
    const header = collapsiblePanel(this.root, 'bestiario', 'Bestiário');

    this.list = document.createElement('div');
    this.list.className = 'bestiary-list';

    this.root.append(header.root, this.list);
    this.render();
  }

  get isOpen(): boolean {
    return this.open;
  }

  toggle(): boolean {
    this.open = !this.open;
    this.render();
    return this.open;
  }

  close(): void {
    this.open = false;
    this.render();
  }

  /** Substitui a ficha inteira. O pacote vem completo, e não em delta. */
  setEntries(packet: BestiaryPacket): void {
    this.list.replaceChildren();

    if (packet.entries.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'bestiary-empty';
      empty.textContent = 'Você ainda não caçou nada. Volte depois do primeiro abate.';
      this.list.append(empty);
      return;
    }

    // Do mais caçado para o menos: é a ordem em que o jogador pensa na própria ficha.
    const ordered = [...packet.entries].sort((a, b) => b.kills - a.kills);

    for (const entry of ordered) {
      this.list.append(this.card(entry));
    }
  }

  private card(entry: BestiaryPacket['entries'][number]): HTMLElement {
    const stage = bestiaryStage(entry.kills);
    const next = BESTIARY_MILESTONES[stage];

    const card = document.createElement('div');
    card.className = 'bestiary-card';

    const head = document.createElement('div');
    head.className = 'bestiary-card-head';

    const name = document.createElement('span');
    name.className = 'bestiary-name';
    name.textContent = entry.name;

    const kills = document.createElement('span');
    kills.className = 'bestiary-kills';
    kills.textContent = next === undefined ? `${entry.kills} — completo` : `${entry.kills} / ${next}`;

    head.append(name, kills);

    const bar = document.createElement('div');
    bar.className = 'bestiary-bar';
    const fill = document.createElement('div');
    fill.className = 'bestiary-bar-fill';
    // Marco completo enche a barra: uma barra a 34% num bicho que o jogador já conhece
    // inteiro leria como progresso que ele perdeu.
    fill.style.width =
      next === undefined
        ? '100%'
        : `${Math.min(100, Math.round((entry.kills / next) * 100))}%`;
    bar.append(fill);

    card.append(head, bar);

    if (entry.maxHealth > 0) {
      card.append(
        line(`Vida ${entry.maxHealth} · ataque ${entry.attackPower}`),
      );
    }

    if (entry.resistances.length > 0) {
      // Só o que **difere** do neutro: listar sete linhas de "100%" é ruído, e o que o
      // jogador procura aqui é a fraqueza.
      const notable = entry.resistances
        .map((value, element) => ({ value, element: element as DamageElement }))
        .filter((row) => row.value !== 100)
        .map((row) => `${ELEMENT_NAMES[row.element]} ${row.value}%`);

      card.append(line(notable.length > 0 ? notable.join(' · ') : 'Sem fraqueza conhecida.'));
    }

    if (next !== undefined) {
      card.append(line(`Aos ${next}: ${BESTIARY_REWARDS[stage] ?? ''}`, 'bestiary-next'));
    }

    return card;
  }

  private render(): void {
    this.root.classList.toggle('visible', this.open);
  }
}

function line(text: string, className = 'bestiary-line'): HTMLElement {
  const element = document.createElement('p');
  element.className = className;
  element.textContent = text;
  return element;
}
